import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';

import { BehaviorSubject, Subject, timer, takeUntil, takeWhile } from 'rxjs';

import { environment } from '@env/environment';

import { EventType, GTMService } from '@personal-area/global/gtm';
import { LocalStorageService } from '@personal-area/storage';

import { BloomreachEventName } from '../models/bloomreach.model';


declare let exponea: any;
const storageStatus = 'notification_status';

@Injectable({
  providedIn: 'root',
})
export class BloomreachService {
  private static _token = environment.bloomreach.token;
  private static _target = environment.bloomreach.target;
  private static _enabled = environment.bloomreach.enable;
  private static _id = 'bloomreach';
  public initialized: BehaviorSubject<boolean>;
  private _ngUnsubscribe: Subject<void>;
  private _renderer: Renderer2;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    rendererFactory: RendererFactory2,
    private _localStorage: LocalStorageService,
    private readonly _gtmService: GTMService
  ) {
    this._renderer = rendererFactory.createRenderer(null, null);
    this._ngUnsubscribe = new Subject<void>();
    this.initialized = new BehaviorSubject<boolean>(false);
  }

  public createSession(): void {
    const s = this._renderer.createElement('script');
    s.id = BloomreachService._id;
    /*eslint-disable */
    s.innerHTML = `
      !function(e,n,t,i,o,r){function a(e){if("number"!=typeof e)return e;var n=new Date;return new Date(n.getTime()+1e3*e)}var c=4e3,s="xnpe_async_hide";function p(e){return e.reduce((function(e,n){return e[n]=function(){e._.push([n.toString(),arguments])},e}),{_:[]})}function m(e,n,t){var i=t.createElement(n);i.src=e;var o=t.getElementsByTagName(n)[0];return o.parentNode.insertBefore(i,o),i}function u(e){return"[object Date]"===Object.prototype.toString.call(e)}r.target=r.target||"https://api.exponea.com",r.file_path=r.file_path||r.target+"/js/exponea.min.js",o[n]=p(["anonymize","initialize","identify","getSegments","update","track","trackLink","trackEnhancedEcommerce","getHtml","showHtml","showBanner","showWebLayer","ping","getAbTest","loadDependency","getRecommendation","reloadWebLayers","_preInitialize"]),o[n].notifications=p(["isAvailable","isSubscribed","subscribe","unsubscribe"]),o[n]["snippetVersion"]="v2.5.0",function(e,n,t){e[n]["_"+t]={},e[n]["_"+t].nowFn=Date.now,e[n]["_"+t].snippetStartTime=e[n]["_"+t].nowFn()}(o,n,"performance"),function(e,n,t,i,o,r){e[o]={sdk:e[i],sdkObjectName:i,skipExperiments:!!t.new_experiments,sign:t.token+"/"+(r.exec(n.cookie)||["","new"])[1],path:t.target}}(o,e,r,n,i,RegExp("__exponea_etc__"+"=([\\\\w-]+)")),function(e,n,t){m(e.file_path,n,t)}(r,t,e),function(e,n,t,i,o,r,p){if(e.new_experiments){!0===e.new_experiments&&(e.new_experiments={});var l,f=e.new_experiments.hide_class||s,_=e.new_experiments.timeout||c,d=encodeURIComponent(r.location.href.split("#")[0]);e.cookies&&e.cookies.expires&&("number"==typeof e.cookies.expires||u(e.cookies.expires)?l=a(e.cookies.expires):e.cookies.expires.tracking&&("number"==typeof e.cookies.expires.tracking||u(e.cookies.expires.tracking))&&(l=a(e.cookies.expires.tracking))),l&&l<new Date&&(l=void 0);var x=e.target+"/webxp/"+n+"/"+r[t].sign+"/modifications.min.js?http-referer="+d+"&timeout="+_+"ms"+(l?"&cookie-expires="+Math.floor(l.getTime()/1e3):"");"sync"===e.new_experiments.mode&&r.localStorage.getItem("__exponea__sync_modifications__")?function(e,n,t,i,o){t[o][n]="<"+n+' src="'+e+'"></'+n+">",i.writeln(t[o][n]),i.writeln("<"+n+">!"+o+".init && document.writeln("+o+"."+n+'.replace("/'+n+'/", "/'+n+'-async/").replace("><", " async><"))</'+n+">")}(x,n,r,p,t):function(e,n,t,i,o,r,a,c){r.documentElement.classList.add(e);var s=m(t,i,r);function p(){o[c].init||m(t.replace("/"+i+"/","/"+i+"-async/"),i,r)}function u(){r.documentElement.classList.remove(e)}s.onload=p,s.onerror=p,o.setTimeout(u,n),o[a]._revealPage=u}(f,_,x,n,r,p,o,t)}}(r,t,i,0,n,o,e),function(e,n,t){var i;(null===(i=t.experimental)||void 0===i?void 0:i.non_personalized_weblayers)&&e[n]._preInitialize(t),e[n].start=function(i){i&&Object.keys(i).forEach((function(e){return t[e]=i[e]})),e[n].initialize(t)}}(o,n,r)}(document,"exponea","script","webxpClient",window,{
        target: "${BloomreachService._target}",
        token: "${BloomreachService._token}",
        service_worker_path: "https://${this._document.domain}/cabinet/ngsw-worker-polyfill.js",
        track: {
          google_analytics: false
        }
      });
      exponea.start();
    `;
    /*eslint-enable */

    this._renderer.insertBefore(this._document.head, s, this._document.head.firstChild);
    this._startInitTimer();
  }

  public identifyUserAndSubscribe(userId: number): void {
    if (BloomreachService._enabled && this.initialized.value) {
      exponea.identify(
        {
          user_id: userId,
        },
        {},
        () => {
          if (this._shouldUpdateStatus) {
            if (Notification.permission === 'granted' && this._isBrowserAvailable) {
              this._subscribe();
              return;
            }

            this._trackConsent(false);
          }
        }
      )
    }
  }

  public trackEvent(name: BloomreachEventName, properties: Record<string, any> = {}): void {
    exponea.track(
      name,
      properties
    );
  }

  private get _shouldUpdateStatus(): boolean {
    return this._localStorage.getItem(storageStatus) !== Notification.permission;
  }

  private get _isBrowserAvailable(): boolean {
    return this.initialized.value && exponea.notifications.isAvailable((available: boolean) => available);
  }

  private _updateStatus(): void {
    this._localStorage.setItem(storageStatus, Notification.permission);
  }

  private _startInitTimer(): void {
    timer(0, 500)
      .pipe(
        takeWhile(count => count <= 40),
        takeUntil(this._ngUnsubscribe)
      )
      .subscribe(() => {
        if (exponea.isInitialized) {
          this.initialized.next(true);
          this._ngUnsubscribe.next();
          this._ngUnsubscribe.complete();
        }
      });
  }

  private _subscribe(): void {
    exponea.notifications.isSubscribed((active: boolean, denied: boolean) => {
      if (denied) {
        return;
      }

      exponea.notifications.subscribe((status: string) => {
        if (status === 'subscribed') {
          this._trackConsent(true);
          this._gtmSubscribe();
        }
      }, {
        push_notifications: true
      });
    });
  }

  private _gtmSubscribe(): void {
    this._gtmService.event(EventType.UAEvent, {
      eventCategory: 'Behavior',
      eventAction: 'Push pop-up subscribe'
    });
  }

  private _trackConsent(consent: boolean): void {
    exponea.track(
      'web-push-consent',
      {
        consent: consent,
        timestamp: Date.now(),
      }
    );

    this._updateStatus();
  }
}
